import { makeStyles } from '@material-ui/styles'
import { DnsRecordType } from 'app/plugins/openstack/components/networks/dns/records/RecordTypePicklist'
import { listRecords } from 'app/plugins/openstack/components/networks/dns/records/actions'
import { recordsSelector } from 'app/plugins/openstack/components/networks/dns/records/selectors'
import { listZones, deleteZone } from 'app/plugins/openstack/components/networks/dns/zones/actions'
import { useAppSelector } from 'app/store'
import clsx from 'clsx'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import React, { useEffect, useMemo, useCallback, useState } from 'react'
import { listNetworks, updateNetwork, updateSubnet } from '../../actions'
import { networksSelector } from '../../selectors'
import { TablePrefsParams, listTablePrefs } from 'app/constants'
import InferActionParams from 'core/actions/InferActionParams'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Grid from 'core/elements/grid'
import { ArrayElement } from 'core/actions/Action'
import Checkbox from 'core/elements/input/Checkbox'

type ActionParams = InferActionParams<typeof listNetworks>
type Params = ActionParams & {}
type SelectorModel = ArrayElement<ReturnType<typeof networksSelector>>

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackNetworks',
  listTablePrefs,
)

const defaultParams = {
  networks: [] as SelectorModel[],
}

const pageSizeOptions = [3, 5, 10].map((value) => ({ value }))

export default function DeleteZoneDialog({ rows, onClose }) {
  const classes = useStyles()
  const [selectAllNetworks, setSelectAllNetworks] = useState(true)

  const { getParamsUpdater, params } = usePrefParams(defaultParams)
  const { reload } = useListAction(listZones)
  const { loading } = useListAction(listRecords)
  const { loading: loadingNetworks } = useListAction(listNetworks, { params: {} })

  const networks = useAppSelector(networksSelector)
  const records = useAppSelector(recordsSelector)

  const { update, updating, error } = useUpdateAction(deleteZone)
  const {
    bulkUpdate: updateNetworkAction,
    updating: updatingNetwork,
    error: errorNetwork,
  } = useUpdateAction(updateNetwork)

  const {
    bulkUpdate: updateSubnetAction,
    updating: updatingSubnet,
    error: errorSubnet,
  } = useUpdateAction(updateSubnet)

  const columns = [
    {
      key: 'name',
      label: 'Network Name',
      render: (name) => <Text variant="caption1">{name}</Text>,
    },
    {
      key: 'type',
      label: 'Network Type',
      render: (type) => <Text variant="caption1">{type}</Text>,
    },
    ...(rows?.length > 1
      ? [
          {
            key: 'dns_domain',
            label: 'DNS Zone',
            render: (dns_domain) => <Text variant="caption1">{dns_domain}</Text>,
          },
        ]
      : []),
  ]

  // Partition zones into deletable vs blocked
  const { zonesWithRecords, zonesWithoutRecords } = useMemo(() => {
    const withRecords: typeof rows = []
    const withoutRecords: typeof rows = []

    rows.forEach((zone) => {
      const zoneRecords = records?.filter((record) => record?.zoneId === zone?.id) ?? []
      const hasBlockingRecords = zoneRecords.some(
        (record) => record.type !== DnsRecordType.NS && record.type !== DnsRecordType.SOA,
      )

      if (hasBlockingRecords) {
        withRecords.push(zone)
      } else {
        withoutRecords.push(zone)
      }
    })

    return { zonesWithRecords: withRecords, zonesWithoutRecords: withoutRecords }
  }, [rows, records])

  const networksUsingCurrentZones = useMemo(() => {
    return (
      networks?.filter((network) =>
        zonesWithoutRecords.some(
          (zone) => zone?.name?.toLowerCase() === network?.dns_domain?.toLowerCase(),
        ),
      ) ?? []
    )
  }, [networks, zonesWithoutRecords])

  const allNetworksSelected = useMemo(() => {
    return (params?.networks as SelectorModel[])?.length === networksUsingCurrentZones.length
  }, [networksUsingCurrentZones, params?.networks])

  useEffect(() => {
    if (selectAllNetworks) {
      getParamsUpdater('networks')(networksUsingCurrentZones)
    } else {
      getParamsUpdater('networks')([])
    }
  }, [selectAllNetworks])

  const handleDelete = useCallback(async () => {
    // same delete logic, but now loop over multiple zones
    const subnetIds = (params.networks as SelectorModel[])?.flatMap(
      (network) =>
        network?.subnetDetails
          ?.filter((subnet) => subnet?.dns_publish_fixed_ip)
          ?.map((subnet) => subnet.id) || [],
    )

    const subnetsToUpdate = subnetIds.map((subnetId) => ({
      id: subnetId,
      body: { subnet: { dns_publish_fixed_ip: false } },
    }))

    const subnetResults = await updateSubnetAction(subnetsToUpdate)
    if (!subnetResults?.every((r) => r.success)) return

    const networkToUpdate = (params?.networks as SelectorModel[]).map((network) => ({
      id: network?.id,
      body: { network: { dns_domain: '' } },
    }))

    const networkResults = await updateNetworkAction(networkToUpdate)
    if (!networkResults?.every((r) => r.success)) return

    // delete all zones without records
    for (const zone of zonesWithoutRecords) {
      const { success } = await update({ id: zone?.id })
      if (!success) return
    }

    onClose(true)
    reload(true)
  }, [
    params.networks,
    updateSubnetAction,
    updateNetworkAction,
    update,
    zonesWithoutRecords,
    reload,
    onClose,
  ])

  if (loading) return null

  return (
    <>
      {zonesWithRecords.length > 0 && (
        <Modal
          panel="dialog"
          className={classes.deleteDialog}
          title={
            <div className={classes.titleContainer}>
              <div className={classes.title}>
                <FontAwesomeIcon solid className={clsx(classes.circleIcon, classes.circleError)}>
                  circle-xmark
                </FontAwesomeIcon>
                <Text variant="subtitle2">Deleting Zone</Text>
              </div>
              <FontAwesomeIcon onClick={onClose} className={classes.closeIcon}>
                xmark
              </FontAwesomeIcon>
            </div>
            //   )
          }
          onClose={onClose}
          open
          footer={
            <Button variant="tertiary" onClick={onClose} disabled={updating}>
              Cancel
            </Button>
          }
        >
          <>
            <Text variant="body2">
              You cannot delete this DNS Zone because it contains active DNS Records. Please delete
              all associated Records before deleting the Zone.
            </Text>
            <div className={classes.nameConatainer}>
              {zonesWithRecords.map((zone) => (
                <Text key={zone?.id} variant="caption1">
                  {zone?.name}
                </Text>
              ))}
            </div>
          </>
        </Modal>
      )}

      {zonesWithoutRecords.length > 0 && (
        <ConfirmationDialog
          open
          maxWidth={800}
          loading={updating || updatingNetwork || updatingSubnet}
          text={
            <>
              <Text variant="body2">
                Deleting the following DNS zone(s) will remove all DNS records under them. This
                action cannot be undone.
              </Text>
              <ul>
                {zonesWithoutRecords.map((zone) => (
                  <li key={zone?.id}>
                    <Text variant="caption1">{zone?.name}</Text>
                  </li>
                ))}
              </ul>

              <>
                <Text variant="caption1">Networks using these zones:</Text>
                <Text variant="body2">
                  Select the networks from which you want to unassign the zone. If you skip, those
                  networks will still refer to stale entries.
                </Text>
                <br />
                {networksUsingCurrentZones?.length > 0 && (
                  <Checkbox
                    label="Select All Networks Using These Zones"
                    checked={allNetworksSelected}
                    onChange={() => setSelectAllNetworks(!selectAllNetworks)}
                    id="selectAllNetworks"
                  />
                )}
                <div className={classes.container}>
                  <Grid<SelectorModel>
                    uniqueIdentifier="id"
                    columns={columns}
                    data={networksUsingCurrentZones}
                    label="Networks"
                    orderBy="name"
                    orderDirection="asc"
                    emptyContent="No Networks are mapped to these zones"
                    disableRowSelection={false}
                    multiSelection
                    selectedItems={params?.networks as SelectorModel[]}
                    onSelectChange={(selectedItems) => getParamsUpdater('networks')(selectedItems)}
                    disableColumnHiding
                    loading={loadingNetworks}
                    rowsPerPage={3}
                    pageSizeOptions={pageSizeOptions}
                  />
                </div>
              </>
            </>
          }
          onCancel={onClose}
          onConfirm={handleDelete}
          error={error || errorNetwork || errorSubnet}
        />
      )}
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '338px',
  },
  circleIcon: {
    fontSize: '24px',
    color: '#fff',
  },
  circleError: {
    background: theme.palette.graphRed[100],
    color: theme.palette.graphRed[500],
  },
  deleteDialog: {
    width: '338px',
    '& header': {
      width: '394px',
      border: 'none',
    },
    '& .modal-body': {
      paddingTop: theme.spacing(1),
    },
    '& footer': {
      border: 'none',
      justifyContent: 'unset',
    },
  },
  nameConatainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  closeIcon: {
    cursor: 'pointer',
  },
  container: {
    marginTop: theme.spacing(2),
  },
  radioLabelContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    overflowY: 'auto',
    maxHeight: '376px',
    marginTop: theme.spacing(2),
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px',
  },
}))
